import { memo, useMemo, useRef, useState, useContext } from 'react';
import ControlScrollBtns, { IControlScrollBtnsRef, TDirection } from './control-scroll-btns';
import {
    QSvg,
    JumpLinkOfQuestionSourceSvg,
    KnowledgeSliceVideoIconSvg,
    KnowledgeSliceDocumentIconSvg,
} from '@/sub-luca-plugin/compatible-components/svg-icons';
import { IKnowledgeSectionItem, ISubjectSearch } from '@/sub-luca-plugin/types/models/qa';
import { eventBusExample, navigateExample } from '@/sub-luca-plugin/utils/class-example';
import {
    Swiper,
    SwiperSlide,
    SwiperPagination,
    ScrollView,
    IScrollViewContext,
    IOnScrollData,
    Image,
    AnimationContainer,
} from '@/sub-luca-plugin/compatible-components';
import { getClientRectInfo } from '@/sub-luca-plugin/utils';
import { check_care_auth } from '@/sub-luca-plugin/apis/qa';
import { QaRecordsContext } from '../../helper';
import { clickSubjectSearchDataSensors } from '@/sub-luca-plugin/utils/sensors';
import classNames from 'classnames';
import './index.scss';

const SubjectSearch = ({ recordId, qaList, knowledgeSectionList }: Required<ISubjectSearch>) => {
    const { getQaStoreByContext } = useContext(QaRecordsContext);
    const scrollContainer = useRef<IScrollViewContext>();
    const controlScrollBtnsRef = useRef<IControlScrollBtnsRef>();
    const [activeIndex, setActiveIndex] = useState<number>();

    const newQaList = useMemo(() => {
        if (!qaList.length) return [];
        return new Array(Math.ceil(qaList.length / 3)).fill(null).map((_, index) => {
            return qaList.slice(index * 3, (index + 1) * 3);
        });
    }, [qaList]);

    const handleViewKnowledgeSliceDetail = async (item: IKnowledgeSectionItem, index: number) => {
        setActiveIndex(index);
        const { projectId, subjectId, twoLevelSubjectId, courseId } = getQaStoreByContext()!.qaConfig;
        const { result } = await check_care_auth({ projectId: projectId!, subjectId: subjectId!, courseId });
        clickSubjectSearchDataSensors({ dataType: item.resourceType === 0 ? '视频' : '知识点', havePermission: result, recordId });
        if (!result) {
            eventBusExample.publish('WarningModal', { type: 'no_view_permission' });
            return;
        }
        if (item.resourceType === 0) {
            // console.log(item);
            navigateExample.linkTo('video', {
                title: item.title,
                videoId: item.resource,
                autoplay: true,
                recordId: item.recordId,
                projectId,
                subjectId,
                twoLevelSubjectId,
            });
        } else {
            navigateExample.linkTo('knowledge_points_detail', {
                title: item.title,
                content: item.resource,
                recordId: item.recordId,
                projectId,
                subjectId,
                twoLevelSubjectId,
            });
        }
    };

    const handleControlScroll = async (direction: TDirection) => {
        const { scrollLeft, scrollWidth } = await getClientRectInfo(scrollContainer.current?.node);
        let scrollDistance = scrollLeft;
        if (direction === 'left') {
            scrollDistance -= 140;
            scrollDistance = scrollDistance < 0 ? 0 : scrollDistance;
        } else {
            scrollDistance += 140;
            scrollDistance = scrollDistance > scrollWidth ? scrollWidth : scrollDistance;
        }
        scrollContainer.current?.scrollTo({ left: scrollDistance, animation: true });
    };

    const handleScrollX = ({ scrollLeft, scrollWidth, clientWidth }: IOnScrollData) => {
        // const { scrollLeft, clientWidth, scrollWidth } = scrollContainer.current!;
        // console.log('handleScrollX', scrollLeft, scrollLeft + clientWidth, scrollWidth);
        controlScrollBtnsRef.current?.controlLeftBtn(scrollLeft > 10);
        // 这里减1是为了解决在windows电脑上，左侧结果存在浮点数的问题
        controlScrollBtnsRef.current?.controlRightBtn(scrollLeft + clientWidth < scrollWidth - 1);
    };

    const handleResizeChange = async (data: IOnScrollData) => {
        const { clientWidth, scrollWidth } = data;
        if (clientWidth === scrollWidth) {
            controlScrollBtnsRef.current?.controlLeftBtn(false);
            controlScrollBtnsRef.current?.controlRightBtn(false);
        } else {
            handleScrollX(data);
        }
    };

    const handleViewDetail = (recordId: string, qaId: string) => {
        clickSubjectSearchDataSensors({ dataType: 'QA', havePermission: true, recordId });
        // 跳转
        navigateExample.linkTo('qa_pair_detail', { id: qaId });
    };

    return (
        <div className="respondent-container">
            <AnimationContainer
                className="subject-search"
                type="enter_from_the_left"
                canExcuteAimation={getQaStoreByContext()?.qaConfig.canExcuteAimation}
            >
                <div className="subject-search__swiper">
                    <div className="subject-search__title">学科搜索</div>
                    {newQaList.length > 0 && (
                        <Swiper
                            spaceBetween={30}
                            pagination={{
                                clickable: true,
                            }}
                            modules={[SwiperPagination]}
                            className="subject-search__swiper"
                        >
                            {newQaList.map((childList, index) => {
                                const key = index.toString();
                                return (
                                    <SwiperSlide key={key}>
                                        {childList.map((childItem, childIndex) => (
                                            <div
                                                key={childItem.qaId}
                                                className="subject-search__item"
                                                onClick={() => handleViewDetail(recordId, childItem.qaId)}
                                            >
                                                <QSvg className="subject-search__item-icon1" />
                                                <div
                                                    className={classNames(
                                                        'subject-search__content',
                                                        childIndex < childList.length - 1 && 'subject-search__content--border-bottom'
                                                    )}
                                                >
                                                    <span>{childItem.qaTitle}</span>
                                                </div>
                                                <JumpLinkOfQuestionSourceSvg className="subject-search__item-icon2" />
                                            </div>
                                        ))}
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                    )}
                </div>
                {knowledgeSectionList.length > 0 && (
                    <>
                        <div className="subject-search__knowledge-slice-title" />
                        <div className="subject-search__knowledge-slice-container">
                            {/* 该组件只针对PC端有效 */}
                            <ControlScrollBtns ref={controlScrollBtnsRef} onScrollTo={handleControlScroll} />
                            <ScrollView
                                className="subject-search__knowledge-slice-scroll-container"
                                scrollX
                                ref={scrollContainer}
                                onScroll={handleScrollX}
                                onSizeChange={handleResizeChange}
                            >
                                <div className="subject-search__knowledge-slice">
                                    {knowledgeSectionList.map((item, index) => {
                                        return (
                                            <div
                                                key={item.recordId}
                                                className={classNames(
                                                    'subject-search__knowledge-slice-item',
                                                    index === activeIndex && 'subject-search__knowledge-slice-item--actived'
                                                )}
                                                onClick={() => handleViewKnowledgeSliceDetail(item, index)}
                                            >
                                                <div
                                                    className={classNames(
                                                        'subject-search__knowledge-slice-cover-container',
                                                        item.resourceType === 0 && 'subject-search__knowledge-slice-cover-container--video'
                                                    )}
                                                >
                                                    <Image
                                                        src={item.coverImageUrl}
                                                        className="subject-search__knowledge-slice-cover"
                                                    ></Image>
                                                </div>
                                                <div className="subject-search__knowledge-slice-describe">
                                                    <div className="subject-search__knowledge-slice-describe-icon-container">
                                                        {item.resourceType === 0 && (
                                                            <KnowledgeSliceVideoIconSvg className="subject-search__knowledge-slice-describe-icon" />
                                                        )}
                                                        {item.resourceType === 1 && (
                                                            <KnowledgeSliceDocumentIconSvg className="subject-search__knowledge-slice-describe-icon" />
                                                        )}
                                                    </div>
                                                    {item.title}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollView>
                        </div>
                    </>
                )}
            </AnimationContainer>
        </div>
    );
};

export default memo(SubjectSearch);
